import { useCallback, useEffect, useState } from 'react';
import { AxiosResponse } from 'axios';
import { ApiError } from '@/openapi';

type RemoteMethod<T> = ((id: string | number) => Promise<AxiosResponse<T> | T | void>) | undefined;

export const useRemoteDelete = <T>({
	remoteMethod,
	lazy = true
}: {
	remoteMethod: RemoteMethod<T>;
	lazy?: boolean;
}) => {
	const [data, setData] = useState<T | undefined>(undefined);
	const [loading, setLoading] = useState<boolean>(false);
	const [error, setError] = useState<string | undefined>(undefined);
	const [errorDetail, setErrorDetail] = useState<string | undefined>(undefined);
	const [errorCode, setErrorCode] = useState<number | undefined>(undefined);
	const [success, setSuccess] = useState<boolean>(false);

	// Metodo per resettare l'errore
	const resetError = () => {
		setError(undefined);
		setErrorDetail(undefined);
		setErrorCode(undefined);
	};

	// Metodo per resettare lo stato
	const resetState = () => {
		setData(undefined);
		setError(undefined);
		setErrorDetail(undefined);
		setErrorCode(undefined);
		setSuccess(false);
	};

	const deleteData = useCallback(
		async (id: string | number) => {
			if (!remoteMethod) {
				setError('Nessun metodo remoto definito');
				return;
			}

			try {
				setLoading(true);
				setSuccess(false);
				const response = await remoteMethod(id);

				// Per le operazioni delete, il server potrebbe restituire void o un 204 No Content
				if (response) {
					if (typeof response === 'object' && 'data' in response) {
						// È un AxiosResponse
						setData(response.data);
					} else {
						// È direttamente il dato
						setData(response as T);
					}
				}

				setSuccess(true);
				setError(undefined);
				setErrorDetail(undefined);
				setErrorCode(undefined);
			} catch (err: any) {
				console.error(err);
				setSuccess(false);
				if (err instanceof ApiError) {
					setError(err.message);
					setErrorDetail(err.body?.message);
					setErrorCode(err.status);
				} else if (err instanceof Error) {
					setError(err.message);
				} else {
					setError("Errore durante l'eliminazione dei dati");
					if (err.body?.message) {
						setErrorDetail(err.body.message);
					}
					if (err.status) {
						setErrorCode(err.status);
					}
				}
			} finally {
				setLoading(false);
			}
		},
		[remoteMethod]
	);

	useEffect(() => {
		// Nel caso di delete, non esegue automaticamente anche se lazy è false
		// poiché è necessario conoscere l'id dell'elemento da eliminare
	}, [lazy, remoteMethod]);

	return {
		data,
		loading,
		error,
		errorDetail,
		errorCode,
		success,
		fetch: deleteData,
		resetError,
		resetState
	};
};
